import { isInteractiveDialog } from "@choose/ui";
import { Button } from "@choose/ui/button";
import { Kbd, KbdGroup } from "@choose/ui/kbd";
import { Popover, PopoverContent, PopoverTrigger } from "@choose/ui/popover";
import {
  Toolbar,
  ToolbarButton,
  ToolbarLink,
  ToolbarSeparator,
} from "@choose/ui/toolbar";
import { useQueryClient } from "@tanstack/react-query";
import type { Row, RowSelectionState } from "@tanstack/react-table";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { features } from "~/components/common/tickets/ticket-list";
import { AssigneeSelector } from "~/components/layout/headers/tickets/assignee-selector";
import { SubStatusSelector } from "~/components/layout/headers/tickets/sub-status-selector";
import type { SupportTicketSubStatus } from "~/graphql/graphql.gen";
import {
  useCurrentUserQuery,
  useInfiniteTicketsQuery,
  useSetAssignedToMutation,
  useSetSubStatusMutation,
} from "~/graphql/graphql.gen";
import { flattenRows } from "~/hooks/use-select-rows";
import { track } from "~/libs/analytics";
import { AGENT_ROLES } from "~/stores/roles-store";
import type { Ticket } from "~/stores/tickets-store";

export const ASSIGNEE_SHORTCUT = "a";
export const DISMISS_SELECTION_SHORTCUT = "escape";
export const SUB_STATUS_SHORTCUT = "s";

export const TicketActionsToolbar = ({
  rows,
  rowSelection,
  setRowSelection,
}: {
  rows: Row<typeof features, Ticket>[];
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const { t } = useTranslation();
  const [openAssignee, setOpenAssignee] = useState(false);
  const [openSubStatus, setOpenSubStatus] = useState(false);
  const { mutateAsync: setAssignedToAsync } = useSetAssignedToMutation();
  const { mutateAsync: setSubStatusAsync } = useSetSubStatusMutation();
  const queryClient = useQueryClient();

  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });

  const selectedCount = Object.keys(rowSelection).length;

  const handleAssigneeChange = async (assigneeId: string | null) => {
    try {
      const selectedRows = Object.keys(rowSelection).map((rowId) =>
        flattenRows(rows).find((row) => row.id === rowId),
      );

      setOpenAssignee(false);
      setRowSelection({});

      await Promise.all(
        selectedRows.map(async (row) => {
          if (!row) return;

          await setAssignedToAsync({
            ticketId: row.original.id,
            actorUserId: assigneeId,
          });
        }),
      );

      void queryClient.invalidateQueries({
        queryKey: useInfiniteTicketsQuery.getKey(),
      });
    } catch {
      toast.error(t("bulk_actions_toolbar.change_assignee.error"));
    } finally {
      track("ChangeAssigneeBulk");
    }
  };

  const handleStatusChange = async (statusId: string) => {
    try {
      const selectedRows = Object.keys(rowSelection).map((rowId) =>
        flattenRows(rows).find((row) => row.id === rowId),
      );

      setOpenSubStatus(false);
      setRowSelection({});

      await Promise.all(
        selectedRows.map(async (row) => {
          if (!row) return;

          await setSubStatusAsync({
            ticketId: row.original.id,
            subStatus: statusId as SupportTicketSubStatus,
          });
        }),
      );

      void queryClient.invalidateQueries({
        queryKey: useInfiniteTicketsQuery.getKey(),
      });
    } catch {
      toast.error(t("bulk_actions_toolbar.change_status.error"));
    } finally {
      track("ChangeStatusBulk");
    }
  };

  const hasAgentRights =
    user?.__typename === "ActorInternalUser" &&
    AGENT_ROLES.includes(user.internalUser.supportRole);

  useHotkeys(
    DISMISS_SELECTION_SHORTCUT,
    (e) => {
      e.preventDefault();

      setRowSelection({});
    },
    {
      enabled: selectedCount > 0,
      ignoreEventWhen: (e) => isInteractiveDialog(e),
    },
  );
  useHotkeys(
    ASSIGNEE_SHORTCUT,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      setOpenAssignee(true);

      track("OpenChangeAssigneeShortcut");
    },
    {
      enabled: hasAgentRights && selectedCount > 0 && !openAssignee,
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );
  useHotkeys(
    SUB_STATUS_SHORTCUT,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      setOpenSubStatus(true);

      track("OpenChangeStatusShortcut");
    },
    {
      enabled: hasAgentRights && selectedCount > 0 && !openSubStatus,
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );

  if (selectedCount === 0) return null;

  return (
    <div className="flex justify-center">
      <Toolbar
        className="fixed bottom-4 z-20 flex min-w-max rounded-md p-2.5"
        aria-label="Ticket bulk actions"
      >
        <ToolbarLink className="pointer-events-none">
          {t("bulk_actions_toolbar.x_selected", {
            count: selectedCount,
          })}
        </ToolbarLink>

        <ToolbarSeparator />

        <Popover open={openSubStatus} onOpenChange={setOpenSubStatus}>
          <ToolbarButton
            render={
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!hasAgentRights}
                  >
                    {t("bulk_actions_toolbar.change_status.label")}
                    <KbdGroup>
                      <Kbd>{SUB_STATUS_SHORTCUT.toUpperCase()}</Kbd>
                    </KbdGroup>
                  </Button>
                }
              />
            }
          />
          <PopoverContent align="start" className="w-max p-0">
            <SubStatusSelector
              value={[]}
              onSelect={(subStatusId) => {
                void handleStatusChange(subStatusId);
              }}
            />
          </PopoverContent>
        </Popover>

        <Popover open={openAssignee} onOpenChange={setOpenAssignee}>
          <ToolbarButton
            render={
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!hasAgentRights}
                  >
                    {t("bulk_actions_toolbar.change_assignee.label")}
                    <KbdGroup>
                      <Kbd>{ASSIGNEE_SHORTCUT.toUpperCase()}</Kbd>
                    </KbdGroup>
                  </Button>
                }
              />
            }
          />
          <PopoverContent align="start" className="w-max p-0">
            <AssigneeSelector
              value={[]}
              onSelect={(assigneeId) => {
                void handleAssigneeChange(assigneeId);
              }}
            />
          </PopoverContent>
        </Popover>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => setRowSelection({})}
          render={
            <Button type="button" size="sm" variant="secondary">
              <KbdGroup>
                <Kbd>Esc</Kbd>
              </KbdGroup>
              {t("bulk_actions_toolbar.clear_selection")}
            </Button>
          }
        />
      </Toolbar>
    </div>
  );
};
